import React, { useState, useEffect, useRef } from 'react';
import { useThemeContext } from '../context/ThemeContext';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	TextField,
	Button,
	Stack,
	Typography,
	Paper,
	Grid,
	Box,
	IconButton,
	Chip
} from '@mui/material';

import CheckBox from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlank from '@mui/icons-material/CheckBoxOutlineBlank';
import CloudUpload from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import PrintIcon from '@mui/icons-material/Print';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { printReceipt } from '../utils/receipt';

const emptyForm = {
	customerName: '',
	customerPhone: '',
	customerPhone2: '',
	customerAddress: '',
	deviceName: '',
	deviceModel: '',
	serialNumber: '',
	faultDescription: '',
	roughCost: '',
	advanceAmount: '',
	willingHandoverDate: '',
	remark: '',
	accessories: { remote: false, bracket: false, charger: false, powerCable: false, other: '' }
};


export default function CreateJobModal({ open, onClose, onCreate, initialRef }) {
	const { isDarkMode } = useThemeContext();
	const [attachments, setAttachments] = useState([]);
	const [referInput, setReferInput] = useState('');
	const [referJobData, setReferJobData] = useState(null);
	const [isSaving, setIsSaving] = useState(false);
	const [printOnSave, setPrintOnSave] = useState(false);
	const [nextJobNumber, setNextJobNumber] = useState('');
	const fileInputRef = useRef(null);

	useEffect(() => {
		if (!open) {
			setAttachments([]);
			setIsSaving(false);
			setPrintOnSave(false);
		} else {
			// Fetch the next job number when modal opens
			fetchNextJobNumber();
		}
	}, [open]);

	const fetchNextJobNumber = async () => {
		try {
			const axios = require('../axios').default;
			const token = JSON.parse(localStorage.getItem('userInfo'))?.token;
			const res = await axios.get('/paneljob', { headers: { Authorization: `Bearer ${token}` } });
			const docs = Array.isArray(res.data) ? res.data : (Array.isArray(res.data?.data) ? res.data.data : []);
			if (docs && docs.length > 0) {
				// Get the highest job number and increment
				const numbers = docs
					.map(d => {
						const num = d.jobNumber ? parseInt(String(d.jobNumber).replace(/\D/g, '')) : null;
						return num;
					})
					.filter(n => n)
					.sort((a, b) => b - a);
				if (numbers.length > 0) {
					setNextJobNumber(String(numbers[0] + 1));
				} else {
					setNextJobNumber('1001');
				}
			} else {
				setNextJobNumber('1001');
			}
		} catch (e) {
			console.warn('Failed to fetch next job number', e);
			setNextJobNumber('');
		}
	};

	const validationSchema = Yup.object({
		customerName: Yup.string().trim().required('Customer name is required'),
		customerPhone: Yup.string().trim().required('Phone number is required').matches(/^[0-9]{10}$/, 'Phone number must be 10 digits'),
		customerPhone2: Yup.string().trim().notRequired().matches(/^[0-9]{10}$/, 'Phone number must be 10 digits', { excludeEmptyString: true }),
		serialNumber: Yup.string().trim().required('Serial number is required'),
		deviceName: Yup.string().trim().required('Device name is required'),
		deviceModel: Yup.string().trim().required('Device model is required'),
		faultDescription: Yup.string().trim().required('Fault description is required'),
		roughCost: Yup.number().typeError('Must be a number').nullable(),
		advanceAmount: Yup.number().typeError('Must be a number').nullable(),
		willingHandoverDate: Yup.string().nullable(),
	});

    

	const formik = useFormik({
		initialValues: emptyForm,
		validationSchema,
		onSubmit: async (values, { resetForm }) => {
			setIsSaving(true);
			const newJob = {
				// include refer fields if present
				referJobNumber: referInput || (referJobData && (referJobData.jobNumber || referJobData._id)) || '',
				referJobId: referJobData && referJobData._id ? referJobData._id : null,
				customerName: values.customerName,
				customer: values.customerName,
				customerPhone: values.customerPhone,
				customerPhone2: values.customerPhone2,
				customerAddress: values.customerAddress,
				deviceName: values.deviceName,
				deviceModel: values.deviceModel,
				serialNumber: values.serialNumber,
				faultDescription: values.faultDescription,
				roughCost: values.roughCost || 0,
				advanceAmount: values.advanceAmount || 0,
				willingHandoverDate: values.willingHandoverDate || null,
				remark: values.remark,
				accessories: values.accessories,
				attachment: [],
				status: 'Pending',
				jobParts: [],
				jobStatusLog: [{ status: 'Pending', updatedAt: new Date(), remark: values.remark || '' }],
				createdAt: new Date().toISOString(),
				createdByName: 'You'
			};

			try {
				// convert attachments to base64 data URLs before sending
				const fileToDataUrl = (file) => new Promise((resolve, reject) => {
					const reader = new FileReader();
					reader.onload = () => resolve(reader.result);
					reader.onerror = (e) => reject(e);
					reader.readAsDataURL(file);
				});
				if (attachments && attachments.length > 0) {
					try {
						const base64s = await Promise.all(attachments.map(f => fileToDataUrl(f)));
						newJob.attachment = base64s; // store as array of data URLs
					} catch (e) {
						console.warn('Failed to convert attachments to base64', e);
					}
				}
				const axios = require('../axios').default;
				const res = await axios.post('/paneljob', { ...newJob });
				setIsSaving(false);
				if (res && res.data) {
					const saved = res.data;
					if (onCreate) onCreate({ ...saved, id: saved._id, ref: saved.jobNumber });
					if (printOnSave) {
						const savedAcc = saved.accessories || {};
						const hasAccData = savedAcc && Object.keys(savedAcc).some((k) => {
							const v = savedAcc[k];
							return v !== null && v !== undefined && v !== false && v !== '';
						});
						const accessories = hasAccData ? savedAcc : newJob.accessories;
						const toPrint = { ...saved, accessories };
						printReceipt(toPrint);
					}
					resetForm();
				} else {
					if (onCreate) onCreate(newJob);
					if (printOnSave) printReceipt(newJob);
				}
			} catch (err) {
				console.error('Failed to save job', err);
				setIsSaving(false);
				if (onCreate) onCreate(newJob);
				if (printOnSave) printReceipt(newJob);
			} finally {
				setPrintOnSave(false);
			}
		}
	});

	useEffect(() => {
		if (!open) formik.resetForm();
	}, [open, formik]);

	const handleFileSelect = (e) => {
		const files = Array.from(e.target.files || []);
		if (files.length === 0) return;
		const combined = [...attachments, ...files].slice(0, 12);
		setAttachments(combined);
	};

	const lookupRefer = async (val) => {
		const term = (val || referInput || '').toString().trim();
		if (!term) return null;
		try {
			const axios = require('../axios').default;
			const token = JSON.parse(localStorage.getItem('userInfo'))?.token;
			const res = await axios.get('/paneljob', { headers: { Authorization: `Bearer ${token}` } });
			const docs = Array.isArray(res.data) ? res.data : (Array.isArray(res.data?.data) ? res.data.data : []);
			const found = (docs || []).find(d => String(d.jobNumber || d._id || d.ref || '').toLowerCase() === term.toLowerCase());
			if (found) {
				setReferJobData(found);
				setReferInput(found.jobNumber || found._id || '');
				// autofill fields if empty
				try {
					if (!formik.values.customerName) formik.setFieldValue('customerName', found.customerName || found.customer || '');
					if (!formik.values.customerPhone) formik.setFieldValue('customerPhone', found.customerPhone || '');
					if (!formik.values.customerAddress) formik.setFieldValue('customerAddress', found.customerAddress || '');
					if (!formik.values.deviceName) formik.setFieldValue('deviceName', found.deviceName || '');
					if (!formik.values.deviceModel) formik.setFieldValue('deviceModel', found.deviceModel || '');
					if (!formik.values.serialNumber) formik.setFieldValue('serialNumber', found.serialNumber || '');
					if (!formik.values.faultDescription) formik.setFieldValue('faultDescription', found.faultDescription || '');
					// accessories may be array
					if (found.accessories && Array.isArray(found.accessories)) {
						const accObj = { remote: false, bracket: false, charger: false, powerCable: false, other: '' };
						found.accessories.forEach(a => {
							if (typeof a === 'string') {
								const k = a.toLowerCase();
								if (k.includes('remote')) accObj.remote = true;
								else if (k.includes('bracket')) accObj.bracket = true;
								else if (k.includes('charger') || k.includes('adapter')) accObj.charger = true;
								else if (k.includes('power') || k.includes('cable')) accObj.powerCable = true;
								else accObj.other = accObj.other ? `${accObj.other}, ${a}` : a;
							}
						});
						formik.setFieldValue('accessories', accObj);
					}
				} catch (e) {}
				return found;
			}
			return null;
		} catch (e) {
			console.warn('Failed to lookup refer job', e);
			return null;
		}
	};

	const removeAttachment = (index) => {
		setAttachments((p) => p.filter((_, i) => i !== index));
	};

    

	const getTextFieldSx = () => ({
		'& .MuiOutlinedInput-root': {
			color: isDarkMode ? '#f1f5f9' : '#000',
			'& fieldset': {
				borderColor: isDarkMode ? '#334155' : '#d1d5db'
			},
			'&:hover fieldset': {
				borderColor: isDarkMode ? '#475569' : '#9ca3af'
			}
		},
		'& .MuiInputBase-input::placeholder': {
			color: isDarkMode ? '#94a3b8' : '#6b7280',
			opacity: 1
		},
		'& .MuiInputLabel-root': {
			color: isDarkMode ? '#cbd5e1' : '#374151'
		}
	});

	return (
	 	<Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
			<DialogTitle sx={{ bgcolor: isDarkMode ? '#1e293b' : '#fff', color: isDarkMode ? '#f1f5f9' : '#000' }}>Create Job</DialogTitle>
			<DialogContent>
				<form onSubmit={formik.handleSubmit}>
				<Paper elevation={0} sx={{ border: isDarkMode ? '1px solid #334155' : '1px solid #e5e7eb', borderRadius: 2, p: 3, bgcolor: isDarkMode ? '#0f172a' : '#fff' }}>
					{/* Job Number Display */}
					<Box mb={3} sx={{ p: 2, bgcolor: isDarkMode ? '#1e293b' : '#f0f9ff', border: isDarkMode ? '1px solid #334155' : '1px solid #bfdbfe', borderRadius: 1 }}>
						<Typography sx={{ color: isDarkMode ? '#94a3b8' : '#6b7280', fontSize: '0.875rem', mb: 0.5 }}>Job Number</Typography>
						<Typography sx={{ color: isDarkMode ? '#f1f5f9' : '#111827', fontSize: '1.25rem', fontWeight: 600 }}>
							{nextJobNumber || 'Loading...'}
						</Typography>
					</Box>

					{/* Refer / Previous Job */}
					<Box mb={2}>
						<TextField
							label="Refer Job Number (previous job)"
							fullWidth
							value={referInput}
							onChange={(e) => setReferInput(e.target.value)}
							onBlur={() => lookupRefer(referInput)}
							placeholder="Enter previous job number (optional)"
						/>
						{referJobData ? (
							<Box sx={{ mt: 1, display: 'flex', gap: 1, alignItems: 'center' }}>
							<Chip 
								label={`Found: ${referJobData.jobNumber || referJobData._id} — ${referJobData.customerName || ''}`} 
								size="small" 
								sx={{ 
									bgcolor: isDarkMode ? '#1e40af' : '#eef2ff',
									color: isDarkMode ? '#f1f5f9' : '#111827',
									fontWeight: 500
								}} 
							/>
								<Button size="small" onClick={() => { setReferJobData(null); setReferInput(''); }} sx={{ textTransform: 'none' }}>Clear</Button>
							</Box>
						) : null}
					</Box>
					{/* Customer Information */}
					<Box mb={3}>
						<Typography sx={{ color: isDarkMode ? '#f1f5f9' : '#111827', mb: 2, fontWeight: 600 }}>Customer Information</Typography>
						<Grid container spacing={2}>
							<Grid item xs={12} md={6}>
								<TextField
									name="customerName"
									label="Customer Name"
									required
									fullWidth
									value={formik.values.customerName}
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									error={formik.touched.customerName && Boolean(formik.errors.customerName)}
									helperText={formik.touched.customerName && formik.errors.customerName}
									sx={{
										'& .MuiOutlinedInput-root': {
											color: isDarkMode ? '#f1f5f9' : '#000',
											'& fieldset': {
												borderColor: isDarkMode ? '#334155' : '#d1d5db'
											},
											'&:hover fieldset': {
												borderColor: isDarkMode ? '#475569' : '#9ca3af'
											}
										},
										'& .MuiInputBase-input::placeholder': {
											color: isDarkMode ? '#94a3b8' : '#6b7280',
											opacity: 1
										},
										'& .MuiInputLabel-root': {
											color: isDarkMode ? '#cbd5e1' : '#374151'
										}
									}}
								/>
							</Grid>

							<Grid item xs={12} md={6}>
								<TextField
									name="customerPhone"
									label="Phone Number"
									required
									fullWidth
									value={formik.values.customerPhone}
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									error={formik.touched.customerPhone && Boolean(formik.errors.customerPhone)}
									helperText={formik.touched.customerPhone && formik.errors.customerPhone}
									sx={{
										'& .MuiOutlinedInput-root': {
											color: isDarkMode ? '#f1f5f9' : '#000',
											'& fieldset': {
												borderColor: isDarkMode ? '#334155' : '#d1d5db'
											},
											'&:hover fieldset': {
												borderColor: isDarkMode ? '#475569' : '#9ca3af'
											}
										},
										'& .MuiInputBase-input::placeholder': {
											color: isDarkMode ? '#94a3b8' : '#6b7280',
											opacity: 1
										},
										'& .MuiInputLabel-root': {
											color: isDarkMode ? '#cbd5e1' : '#374151'
										}
									}}
								/>
							</Grid>

							<Grid item xs={12} md={6}>
									<TextField
										name="customerPhone2"
										label="Phone Number 2"
										fullWidth
										value={formik.values.customerPhone2}
										onChange={formik.handleChange}
										onBlur={formik.handleBlur}
										error={formik.touched.customerPhone2 && Boolean(formik.errors.customerPhone2)}
										helperText={formik.touched.customerPhone2 && formik.errors.customerPhone2}
									/>
							</Grid>

							<Grid item xs={12} md={12}>
								<TextField
									name="customerAddress"
									label="Address"
									fullWidth
									multiline
									rows={2}
									value={formik.values.customerAddress}
									onChange={formik.handleChange}
									sx={getTextFieldSx()}
								/>
							</Grid>
						</Grid>
					</Box>

					{/* Device Information */}
					<Box mt={2} pt={2} borderTop={isDarkMode ? "1px solid #334155" : "1px solid #e5e7eb"}>
						<Typography sx={{ color: isDarkMode ? '#f1f5f9' : '#111827', mb: 2, fontWeight: 600 }}>Device Information</Typography>
						<Grid container spacing={2}>
							<Grid item xs={12} md={12}>
								<TextField
									name="deviceName"
									label="Device Name"
									required
									fullWidth
									value={formik.values.deviceName}
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									error={formik.touched.deviceName && Boolean(formik.errors.deviceName)}
									helperText={formik.touched.deviceName && formik.errors.deviceName}
									placeholder="e.g., LED TV, Washing Machine"
									sx={getTextFieldSx()}
								/>
							</Grid>
							<Grid item xs={12} md={6}>
								<TextField
									name="deviceModel"
									label="Model No"
									required
									fullWidth
									value={formik.values.deviceModel}
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									error={formik.touched.deviceModel && Boolean(formik.errors.deviceModel)}
									helperText={formik.touched.deviceModel && formik.errors.deviceModel}
									placeholder='e.g., Samsung 55" QLED QN55Q60C'
									sx={getTextFieldSx()}
								/>
							</Grid>

							<Grid item xs={12} md={6}>
										<TextField
											name="serialNumber"
											label="Serial Number"
											fullWidth
											value={formik.values.serialNumber}
											onChange={formik.handleChange}
											onBlur={formik.handleBlur}
											error={formik.touched.serialNumber && Boolean(formik.errors.serialNumber)}
											helperText={formik.touched.serialNumber && formik.errors.serialNumber}
											sx={getTextFieldSx()}
										/>
							</Grid>

							<Grid item xs={12} md={12}>
								<TextField
									name="faultDescription"
									label="Fault Description"
									required
									fullWidth
									multiline
									rows={3}
									value={formik.values.faultDescription}
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									error={formik.touched.faultDescription && Boolean(formik.errors.faultDescription)}
									helperText={formik.touched.faultDescription && formik.errors.faultDescription}
									sx={getTextFieldSx()}
								/>
							</Grid>

							<Grid item xs={12} md={4}>
								<TextField name="roughCost" label="Rough Cost" fullWidth value={formik.values.roughCost} onChange={formik.handleChange} sx={getTextFieldSx()} />
							</Grid>

							<Grid item xs={12} md={4}>
								<TextField name="advanceAmount" label="Advance Amount" fullWidth value={formik.values.advanceAmount} onChange={formik.handleChange} sx={getTextFieldSx()} />
							</Grid>

							<Grid item xs={12} md={4}>
								<TextField name="willingHandoverDate" type="date" label="Willing Handover Date" fullWidth InputLabelProps={{ shrink: true }} value={formik.values.willingHandoverDate} onChange={formik.handleChange} sx={getTextFieldSx()} />
							</Grid>

							<Grid item xs={12}>
								<TextField name="remark" label="Remark" fullWidth multiline rows={2} value={formik.values.remark} onChange={formik.handleChange} sx={getTextFieldSx()} />
							</Grid>
						</Grid>
					</Box>

					{/* Accessories */}
					<Box mt={3} pt={3} borderTop={isDarkMode ? "1px solid #334155" : "1px solid #e5e7eb"}>
						<Typography sx={{ color: isDarkMode ? '#f1f5f9' : '#111827', mb: 2, fontWeight: 600 }}>Accessories Present</Typography>
						<Stack spacing={1}>
							{[
								{ key: 'remote', label: 'Remote Control' },
								{ key: 'bracket', label: 'Wall Bracket' },
								{ key: 'charger', label: 'Power Adapter/Charger' },
								{ key: 'powerCable', label: 'Power Cable' }
							].map((a) => (
								<Button
									key={a.key}
									onClick={() => formik.setFieldValue(`accessories.${a.key}`, !formik.values.accessories[a.key])}
									variant={formik.values.accessories[a.key] ? 'contained' : 'outlined'}
									sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
								>
									{formik.values.accessories[a.key] ? <CheckBox sx={{ mr: 1, color: '#2563eb' }} /> : <CheckBoxOutlineBlank sx={{ mr: 1, color: '#94a3b8' }} />}
									{a.label}
								</Button>
							))}

									<TextField label="Other Accessories" fullWidth value={formik.values.accessories.other} onChange={(e) => formik.setFieldValue('accessories.other', e.target.value)} sx={getTextFieldSx()} />
						</Stack>
					</Box>

					{/* Attachments */}
					<Box mt={3} pt={3} borderTop={isDarkMode ? "1px solid #334155" : "1px solid #e5e7eb"}>
						<Typography sx={{ color: isDarkMode ? '#f1f5f9' : '#111827', mb: 1, fontWeight: 600 }}>Attachments</Typography>
						<Typography sx={{ color: isDarkMode ? '#cbd5e1' : '#6b7280', mb: 2 }}>Upload photos of the device and any visible damage (PNG, JPG up to 10MB)</Typography>

						<input ref={fileInputRef} multiple accept="image/*" type="file" hidden onChange={handleFileSelect} />
						<Button startIcon={<CloudUpload />} variant="outlined" onClick={() => fileInputRef.current && fileInputRef.current.click()}>
							Click to upload images
						</Button>

						{attachments.length > 0 && (
							<Grid container spacing={2} sx={{ mt: 2 }}>
								{attachments.map((file, idx) => (
									<Grid item xs={6} sm={4} md={3} key={idx}>
										<Box sx={{ position: 'relative' }}>
											<Box sx={{ width: '100%', aspectRatio: '1 / 1', bgcolor: '#f3f4f6', borderRadius: 1, overflow: 'hidden' }}>
												<img src={URL.createObjectURL(file)} alt={file.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
											</Box>
											<IconButton size="small" onClick={() => removeAttachment(idx)} sx={{ position: 'absolute', top: 6, right: 6, bgcolor: 'rgba(0,0,0,0.55)', color: '#fff' }}>
												<CloseIcon fontSize="small" />
											</IconButton>
											<Typography variant="caption" noWrap sx={{ mt: 1 }}>{file.name}</Typography>
										</Box>
									</Grid>
								))}
							</Grid>
						)}
					</Box>

				</Paper>
				</form>
			</DialogContent>

			<DialogActions sx={{ px: 3, py: 2 }}>
				<Button onClick={() => { formik.resetForm(); onClose && onClose(); }} variant="outlined" sx={{ textTransform: 'none', color: isDarkMode ? '#f1f5f9' : 'inherit', borderColor: isDarkMode ? '#334155' : 'inherit' }}>Cancel</Button>
				<Button startIcon={<SaveIcon />} onClick={() => { setPrintOnSave(false); formik.submitForm(); }} disabled={isSaving} variant="contained" sx={{ bgcolor: '#374151', '&:hover': { bgcolor: '#111827' }, textTransform: 'none' }}>
					{isSaving ? 'Saving...' : 'Save Job'}
				</Button>
				<Button startIcon={<PrintIcon />} onClick={() => { setPrintOnSave(true); formik.submitForm(); }} disabled={isSaving} variant="contained" sx={{ bgcolor: '#2563eb', '&:hover': { bgcolor: '#1d4ed8' }, textTransform: 'none' }}>
					Save & Print Customer Receipt
				</Button>
			</DialogActions>
		</Dialog>
	);
}
